/**
 * Team Design — team-name overlay (orchestrator).
 *
 * The "glue" between the team-name input field and the render + auto layers.
 *
 * Owns:
 *  - Cross-fade CSS (image ↔ preview, not theme swaps)
 *  - IntersectionObserver — tracks which cards are in the viewport
 *  - Grid-level MutationObserver — detects SVG swaps, swatch population,
 *    and new cards added by pagination / AJAX
 *  - Team-name input + clear button binding (with debounce + storage write)
 *  - init()
 *
 * Depends on window.CollectionTeamText.{state, dom, inject, auto} set by
 * team-design.text-render.js and team-design.text-auto.js, which must load
 * first (defer-ordered <script> tags preserve that).
 */
(function () {
  'use strict';

  var NS     = window.CollectionTeamText;
  if (!NS || !NS.state || !NS.dom || !NS.inject || !NS.auto) return;

  var state  = NS.state;
  var dom    = NS.dom;
  var inject = NS.inject;
  var auto   = NS.auto;

  var INPUT_ID    = 'collection-team-name-input';
  var CLEAR_ID    = 'collection-team-name-clear';
  var WRAP_ID     = 'collection-team-name-wrap';
  var COUNTER_ID  = 'collection-team-name-counter';
  var DEBOUNCE_MS = 150;
  var FADE_MS     = 320;
  var MAX_LINES   = 2;
  var MAX_CHARS   = 20;
  var SHAKE_MS    = 200;

  // Collapse runs of newlines, drop a trailing newline (so the input can't sit
  // on a phantom third line), and truncate to MAX_LINES. Leading/trailing
  // whitespace within a line is preserved; only the *outside* of the value is
  // trimmed by callers via .trim().
  function normalizeLines(value) {
    var v = (value || '').replace(/\r\n?/g, '\n');
    var parts = v.split('\n').slice(0, MAX_LINES);
    return parts.join('\n');
  }

  // ─── Cross-fade CSS ───────────────────────────────────────────────────────
  // The media-wrap fade is driven by an attribute selector on the inline
  // `visibility: hidden` style that team-design.swatches.js (and our
  // auto-load) already toggle. The SVG fade is set inline by the
  // MutationObserver only on a true first-show (image → SVG); theme swaps
  // (SVG → SVG) leave the new SVG's animation untouched and render instantly.
  (function injectStyles() {
    if (document.getElementById('ctt-fade-styles')) return;
    var s = document.createElement('style');
    s.id = 'ctt-fade-styles';
    s.textContent =
      '@keyframes ctt-svg-in{from{opacity:0;}to{opacity:1;}}' +
      '.card-product-figma__image-frame .media--transparent{' +
        'transition:opacity ' + FADE_MS + 'ms ease-out;' +
        'will-change:opacity;' +
      '}' +
      '.card-product-figma__image-frame .media--transparent[style*="visibility: hidden"],' +
      '.card-product-figma__image-frame .media--transparent[style*="visibility:hidden"]{' +
        'opacity:0;' +
      '}';
    document.head.appendChild(s);
  })();

  // ─── IntersectionObserver ─────────────────────────────────────────────────
  var intersectionObs = null;

  function setupIntersectionObserver() {
    if (typeof IntersectionObserver === 'undefined') return;
    intersectionObs = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        var card  = entry.target;
        if (entry.isIntersecting) {
          state.visibleCards.add(card);
          // Auto-render on becoming visible if ANY banner intent is set —
          // mirrors the guard in text-auto.js renderAutoPreview so a card
          // scrolled into view after the shopper picked a font/color also
          // gets its overlay painted.
          var hasIntent = (state.autoMode && state.currentText) ||
                          state.bannerTextFont ||
                          state.bannerNumberFont ||
                          state.bannerTheme;
          if (hasIntent) auto.render(card);
        } else {
          state.visibleCards.delete(card);
        }
      }
    }, { rootMargin: '100px 0px' });

    observeAllCards();
  }

  function observeAllCards() {
    if (!intersectionObs) return;
    var cards = document.querySelectorAll('.card-wrapper');
    for (var i = 0; i < cards.length; i++) {
      if (!cards[i]._teamTextObserved) {
        cards[i]._teamTextObserved = true;
        intersectionObs.observe(cards[i]);
      }
      auto.attachThumbHandlers(cards[i]);
    }
  }

  // ─── Grid-level MutationObserver ──────────────────────────────────────────
  function startPreviewObserver() {
    var root = document.querySelector('.product-grid-container') ||
               document.getElementById('product-grid') ||
               document.body;
    if (!root) return;

    var observer = new MutationObserver(function (mutations) {
      var processedPreview = null;
      var sawCardAddition  = false;

      for (var i = 0; i < mutations.length; i++) {
        var t = mutations[i].target;
        if (!t || !t.classList) continue;

        if (t.classList.contains('card-design-preview')) {
          if (processedPreview === t) continue;
          processedPreview = t;
          processPreviewMutation(t, mutations[i]);
        } else if (t.classList.contains('card-color-swatches__colors')) {
          // team-design.swatches.js just populated swatches — render now if
          // we're in auto-mode and the card is visible.
          var card2 = t.closest('.card-wrapper');
          if (card2 && state.autoMode && state.currentText && state.visibleCards.has(card2)) {
            auto.render(card2);
          }
        } else if (t.id === 'product-grid' || t.classList.contains('product-grid-container')) {
          sawCardAddition = true;
        }
      }

      if (sawCardAddition) observeAllCards();
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  function processPreviewMutation(previewEl, mut) {
    // Classify the mutation: first-show (image → SVG) vs theme swap (SVG → SVG).
    var hadSvg = false, gainedSvg = false;
    for (var rn = 0; rn < mut.removedNodes.length; rn++) {
      var n = mut.removedNodes[rn];
      if (n && n.nodeName && n.nodeName.toLowerCase() === 'svg') { hadSvg = true; break; }
    }
    for (var an = 0; an < mut.addedNodes.length; an++) {
      var m = mut.addedNodes[an];
      if (m && m.nodeName && m.nodeName.toLowerCase() === 'svg') { gainedSvg = true; break; }
    }

    inject.injectTeamText(previewEl);

    // First-show only: scope the fade to this single SVG via inline style
    // so subsequent theme swaps don't inherit it.
    if (gainedSvg && !hadSvg) {
      var fresh = dom.findSvg(previewEl);
      if (fresh) fresh.style.animation = 'ctt-svg-in ' + FADE_MS + 'ms ease-out both';
    }

    // If a hover-leave just emptied a preview we'd auto-loaded, re-apply
    // the cached colored SVG synchronously to avoid a flicker.
    if (state.autoMode && state.currentText && !previewEl.firstChild && previewEl._autoColoredSvg) {
      var card = previewEl.closest('.card-wrapper');
      if (
        card &&
        state.visibleCards.has(card) &&
        !card.querySelector('.card-color-swatch--active') &&
        !card._thumbHover
      ) {
        auto.apply(previewEl, dom.getMediaWrap(card), previewEl._autoColoredSvg);
      }
    }
  }

  // ─── Input binding ────────────────────────────────────────────────────────
  function bindInput() {
    var input    = document.getElementById(INPUT_ID);
    if (!input) return;
    var clearBtn = document.getElementById(CLEAR_ID);
    var wrap     = document.getElementById(WRAP_ID);
    var counter  = document.getElementById(COUNTER_ID);

    // Restore persisted team name into the field if it was loaded from storage.
    if (!input.value && state.currentText) input.value = state.currentText;

    // Auto-resize the textarea to match its content height (1 line tall when
    // empty/single-line, 2 lines tall after Enter). Avoids scrollbars and
    // gives the wrapper a tight fit around the text.
    function autosize() {
      input.style.height = 'auto';
      input.style.height = input.scrollHeight + 'px';
    }

    function updateCounter() {
      var len = input.value.length;
      if (counter) {
        counter.textContent = len + '/' + MAX_CHARS;
        counter.classList.toggle('is-limit', len >= MAX_CHARS);
      }
      if (wrap) wrap.classList.toggle('is-limit', len >= MAX_CHARS);
    }

    var shakeTimer = null;
    function flashLimit() {
      if (!wrap) return;
      wrap.classList.remove('is-limit-hit');
      // Force reflow so the animation can restart even on rapid re-triggers.
      void wrap.offsetWidth;
      wrap.classList.add('is-limit-hit');
      if (shakeTimer) clearTimeout(shakeTimer);
      shakeTimer = setTimeout(function () {
        shakeTimer = null;
        wrap.classList.remove('is-limit-hit');
      }, SHAKE_MS);
    }

    function syncFromInput() {
      var normalized = normalizeLines(input.value);
      if (normalized !== input.value) input.value = normalized;
      autosize();
      updateCounter();
    }

    syncFromInput();
    state.currentText = (input.value || '').trim();
    state.autoMode    = state.currentText.length > 0;

    if (clearBtn) clearBtn.style.display = state.currentText ? '' : 'none';

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        input.dispatchEvent(new Event('input'));
        input.focus();
      });
    }

    // Arming the auto-load pipeline on focus is what lets cookie-restored
    // team text actually paint cards. Before this, focusing the input
    // without typing was a no-op visually; now it triggers the same SVG
    // render path that typing (or a color/font pick) would.
    input.addEventListener('focus', function () {
      var ns = window.CollectionTeamText;
      if (ns && ns.arm) ns.arm();
    });

    // beforeinput fires before the value mutates and exposes the inserted
    // text + the inputType. We use it to flash the input when an edit would
    // exceed MAX_CHARS or push past MAX_LINES — the maxlength attribute
    // silently truncates, which leaves users wondering why their keystroke
    // did nothing.
    input.addEventListener('beforeinput', function (e) {
      var t = e.inputType || '';
      // Ignore deletions / non-insertions — those can't exceed limits.
      if (t.indexOf('insert') !== 0) return;

      var insertion = e.data;
      if (insertion == null && e.dataTransfer) insertion = e.dataTransfer.getData('text');
      if (!insertion) return;

      var selStart = input.selectionStart | 0;
      var selEnd   = input.selectionEnd   | 0;
      var current  = input.value.length;
      var selLen   = Math.max(0, selEnd - selStart);
      var nextLen  = current - selLen + insertion.length;

      var nextLines = (
        input.value.slice(0, selStart) + insertion + input.value.slice(selEnd)
      ).split(/\r\n?|\n/).length;

      if (nextLen > MAX_CHARS || nextLines > MAX_LINES) flashLimit();
    });

    // Block a third line at the keydown level — maxlength still caps the
    // overall length, so this only fires when the user tries to add a line
    // they wouldn't otherwise hit the char limit on.
    input.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var lines = input.value.split('\n').length;
      if (lines >= MAX_LINES) {
        e.preventDefault();
        flashLimit();
      }
    });

    var timer = null;
    input.addEventListener('input', function () {
      syncFromInput();
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        timer = null;
        var next = (input.value || '').trim();
        if (clearBtn) clearBtn.style.display = next.length > 0 ? '' : 'none';
        if (next === state.currentText) return;

        var prevAuto = state.autoMode;
        state.currentText = next;
        state.autoMode    = next.length > 0;
        if (window.TeamDesignStorage) window.TeamDesignStorage.setName(next);

        // First keystroke entering the customization flow → promote the
        // banner picker's API-declared default font to active so the
        // overlay renders in the configured font instead of the Anton
        // fallback. No-op if the shopper already picked a font, or if
        // the banner picker isn't on this page.
        if (state.autoMode) {
          var qc = window.CollectionQuickCustomization;
          if (qc && qc.selectDefaultFont) qc.selectDefaultFont();
        }

        // Order matters: clear stale auto-loads BEFORE refreshing previews,
        // otherwise refresh would briefly inject text on previews we're
        // about to wipe.
        if (!state.autoMode && prevAuto) auto.clearAll();

        // Update injected text on any still-visible previews (hover or auto).
        inject.refreshAllVisiblePreviews();

        // Newly turned on → render every visible card.
        if (state.autoMode && !prevAuto) auto.renderVisible();
      }, DEBOUNCE_MS);
    });
  }

  // ─── Init ─────────────────────────────────────────────────────────────────
  function init() {
    bindInput();
    setupIntersectionObserver();
    startPreviewObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();